import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { API_BASE } from "../services/server";
import { useAuth } from "../services/AuthContext";
import Main from "./Main";
import DataRoom, { QuestionsPanel } from "./dataroom";
import { makeBuyerNav } from "./nav";
import { DDQ_SECTIONS } from "../config/questionSchemas";
import "./css/seller-portal.css";
import "./css/dataroom.css";

async function apiFetch(path) {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
  return res;
}

// ─── Valuation module ────────────────────────────────────────────────────────

function ValuationModule({ workspaceKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/valuation/workspaces/${workspaceKey}/summary/`)
      .then(async (res) => {
        if (res.status === 404 || res.status === 204) return null;
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || "Could not load valuation.");
        }
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [workspaceKey]);

  return (
    <section className="sp-module">
      <h2 className="sp-module-title">Valuation</h2>
      <p className="sp-module-desc">
        Read-only view of the company valuation prepared by the broker.
      </p>
      {loading && <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</p>}
      {error && <p className="sp-error">{error}</p>}
      {!loading && !error && !data && (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>
          No valuation data has been published yet.
        </p>
      )}
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {Object.entries(data).map(([key, value]) =>
            typeof value === "object" || value === null ? null : (
              <div
                key={key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                <span style={{ color: "#6b7280", textTransform: "capitalize" }}>
                  {key.replace(/_/g, " ")}
                </span>
                <span style={{ fontWeight: 600, color: "#111827" }}>
                  {String(value)}
                </span>
              </div>
            )
          )}
        </div>
      )}
    </section>
  );
}

// ─── Data room module ────────────────────────────────────────────────────────
//
// Uses the broker's full DataRoom component in read-only mode. The component
// reads :company from the URL, which the portal's route already provides, so
// we just render it with viewerRole="buyer" and chromeless so it nests inside
// our existing <Main> layout without double-wrapping.

function DataRoomModule() {
  return (
    <section className="sp-module" style={{ padding: 0 }}>
      <DataRoom viewerRole="buyer" chromeless />
    </section>
  );
}

// ─── Due diligence module ────────────────────────────────────────────────────

function DDModule({ workspaceKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/users/buyer-portal/${workspaceKey}/dd/`)
      .then(async (res) => {
        if (res.status === 204) return null;
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || "Kunde inte hämta DD-svar.");
        }
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [workspaceKey]);

  // Backend returnerar {finalized: bool, answers: {qid: {status, notes,
  // attachments}}}. Status är ok/na/todo (svenska etiketter nedan).
  const STATUS_META = {
    ok:   { label: "Finns / klar", bg: "#ECFDF5", color: "#047857" },
    na:   { label: "Ej tillgängligt", bg: "#F3F4F6", color: "#6B7280" },
    todo: { label: "Pågår", bg: "#FEF3C7", color: "#92400E" },
  };

  // Normalize the answers payload. New backends return
  //   answers = { qid: {status, notes, attachments}, ... }
  // Older backends still return the flat-array shape
  //   answers = [{qid, status, notes, attachments}, ...]
  // Both need to render the same way, so we key the array by qid
  // before it hits the sections/summary reducers.
  const answersByQid = useMemo(() => {
    const raw = data?.answers;
    if (!raw) return null;
    if (Array.isArray(raw)) {
      const out = {};
      for (const item of raw) {
        if (item && item.qid) out[item.qid] = item;
      }
      return out;
    }
    return raw;
  }, [data?.answers]);

  // Bygg en lista av {qid, label, status, notes, attachments} i samma
  // ordning som questionSchemas.js för att gruppera per sektion.
  const sections = useMemo(() => {
    if (!answersByQid) return [];
    return DDQ_SECTIONS.map((s) => ({
      id: s.id,
      title: s.title,
      items: (s.items || []).map((q) => {
        const a = answersByQid[q.qid] || {};
        return {
          qid: q.qid,
          label: q.text,
          status: a.status || "todo",
          notes: a.notes || "",
          attachments: Array.isArray(a.attachments) ? a.attachments : [],
        };
      }),
    })).filter((s) => s.items.length > 0);
  }, [answersByQid]);

  // Räkna ut hur många frågor som är besvarade ("ok" eller "na").
  const summary = useMemo(() => {
    if (!answersByQid) return { total: 0, completed: 0 };
    let total = 0; let completed = 0;
    DDQ_SECTIONS.forEach((s) => (s.items || []).forEach((q) => {
      total += 1;
      const st = answersByQid[q.qid]?.status;
      if (st === "ok" || st === "na") completed += 1;
    }));
    return { total, completed };
  }, [answersByQid]);

  const hasAny = answersByQid && Object.keys(answersByQid).length > 0;

  return (
    <section className="sp-module">
      <h2 className="sp-module-title">Due diligence</h2>
      <p className="sp-module-desc">
        Säljarens svar på DD-checklistan.
      </p>
      {loading && <p style={{ color: "#9ca3af", fontSize: 13 }}>Laddar…</p>}
      {error && <p className="sp-error">{error}</p>}
      {!loading && !error && !hasAny && (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>
          Inga DD-svar har lämnats ännu.
        </p>
      )}
      {!loading && !error && hasAny && (
        <>
          <div style={{
            display: "inline-block",
            padding: "6px 12px",
            background: "#F3F4F6",
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 16,
          }}>
            <strong>{summary.completed}</strong> av <strong>{summary.total}</strong> frågor besvarade
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {sections.map((sec) => (
              <div key={sec.id}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px", color: "#111827" }}>
                  {sec.title}
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {sec.items.map((item) => {
                    const meta = STATUS_META[item.status] || STATUS_META.todo;
                    return (
                      <div
                        key={item.qid}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          padding: "8px 12px",
                          background: "#fafafa",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                        }}
                      >
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: meta.bg,
                          color: meta.color,
                          whiteSpace: "nowrap",
                          marginTop: 1,
                        }}>
                          {meta.label}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: "#374151" }}>
                            <span style={{ fontWeight: 600, marginRight: 6 }}>{item.qid}</span>
                            {item.label}
                          </div>
                          {item.notes && (
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, whiteSpace: "pre-wrap" }}>
                              {item.notes}
                            </div>
                          )}
                          {item.attachments.length > 0 && (
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                              📎 {item.attachments.length} bilag{item.attachments.length === 1 ? "a" : "or"}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ─── Information memorandum module ──────────────────────────────────────────

function IMModule({ workspaceKey }) {
  const [imData, setImData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/users/buyer-portal/${workspaceKey}/im/`)
      .then(async (res) => {
        if (res.status === 204) return null;
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || "Could not load IM.");
        }
        return res.json();
      })
      .then(setImData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [workspaceKey]);

  return (
    <section className="sp-module">
      <h2 className="sp-module-title">Information memorandum</h2>
      <p className="sp-module-desc">
        The broker-approved information memorandum for this company.
      </p>
      {loading && <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading…</p>}
      {error && <p className="sp-error">{error}</p>}
      {!loading && !error && !imData && (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>
          The information memorandum has not been shared yet.
        </p>
      )}
      {imData && imData.url && (
        <div>
          <a
            href={imData.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              padding: "9px 18px",
              background: "#111827",
              color: "#fff",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            Open information memorandum ↗
          </a>
        </div>
      )}
      {imData && imData.sections && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {imData.sections.map((section, i) => (
            <div
              key={i}
              style={{
                padding: "16px 18px",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#fafafa",
              }}
            >
              {section.title && (
                <div
                  style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: "#111827" }}
                >
                  {section.title}
                </div>
              )}
              <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap" }}>
                {section.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Main portal ─────────────────────────────────────────────────────────────

/* ─────────────────────────────────────────────────────────
   Layout + per-module pages
───────────────────────────────────────────────────────── */

export default function BuyerPortalLayout() {
  const { company } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate("/login");
    } catch {
      setLoggingOut(false);
    }
  }

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/users/buyer-portal/${company}/`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Could not load workspace.");
        return data;
      })
      .then(setInfo)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [company]);

  const navItems = React.useMemo(
    () => makeBuyerNav(company, info?.modules || []),
    [company, info?.modules],
  );

  if (loading) {
    return (
      <Main navItems={navItems}>
        <div className="sp-shell"><div className="sp-loading">Loading…</div></div>
      </Main>
    );
  }

  if (error) {
    return (
      <Main navItems={navItems}>
        <div className="sp-shell">
          <div className="sp-error" style={{ padding: "48px 32px", textAlign: "center" }}>{error}</div>
        </div>
      </Main>
    );
  }

  const { workspace_name, buyer_company_name } = info || {};
  const email = user?.user?.email || user?.user?.username || user?.email || user?.username || "";

  return (
    <Main navItems={navItems}>
      <div className="sp-shell">
        <header
          className="sp-header"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}
        >
          <div>
            <div className="sp-header-title">{workspace_name}</div>
            <div className="sp-header-sub">{buyer_company_name} — Buyer portal</div>
          </div>
          <div style={{ textAlign: "right", fontSize: 13, color: "#6b7280" }}>
            <div style={{ marginBottom: 6 }}>{email}</div>
            <button
              type="button"
              onClick={handleLogout}
              disabled={loggingOut}
              style={{
                background: "transparent",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                cursor: loggingOut ? "not-allowed" : "pointer",
                color: "#374151",
              }}
            >
              {loggingOut ? "Loggar ut…" : "Logga ut"}
            </button>
          </div>
        </header>

        <main className="sp-main">
          <Outlet context={{ workspaceKey: company, info }} />
        </main>
      </div>
    </Main>
  );
}

function useBuyerCtx() {
  return useOutletContext();
}

function NoAccessMessage({ children }) {
  return <p className="sp-empty">{children}</p>;
}

export function BuyerDataroomPage() {
  const { info } = useBuyerCtx();
  if (!info?.modules?.includes("dataroom")) {
    return <NoAccessMessage>Du har inte åtkomst till datarummet.</NoAccessMessage>;
  }
  return (
    <section className="sp-module" style={{ padding: 0 }}>
      <DataRoom viewerRole="buyer" chromeless />
    </section>
  );
}

export function BuyerQAPage() {
  const { workspaceKey } = useBuyerCtx();
  return <QuestionsPanel slug={workspaceKey} viewerRole="buyer" embedded />;
}

export function BuyerValuationPage() {
  const { workspaceKey, info } = useBuyerCtx();
  if (!info?.modules?.includes("valuation")) {
    return <NoAccessMessage>Du har inte åtkomst till värdering.</NoAccessMessage>;
  }
  return <ValuationModule workspaceKey={workspaceKey} />;
}

export function BuyerIMPage() {
  const { workspaceKey, info } = useBuyerCtx();
  if (!info?.modules?.includes("im")) {
    return <NoAccessMessage>Du har inte åtkomst till Info memo.</NoAccessMessage>;
  }
  return <IMModule workspaceKey={workspaceKey} />;
}

export function BuyerDDPage() {
  const { workspaceKey, info } = useBuyerCtx();
  if (!info?.modules?.includes("dd")) {
    return <NoAccessMessage>Du har inte åtkomst till Due diligence.</NoAccessMessage>;
  }
  return <DDModule workspaceKey={workspaceKey} />;
}
